import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Persist a personal viral clip when a user taps "Crear Mi Clip Viral".
 * Maps the PersonalClipPayload into the existing public.viral_clips schema.
 * No new tables — uses the columns already present (home_team, away_team,
 * score, minute, emotion, platform). RLS enforces user_id = auth.uid().
 */

const Input = z.object({
  kind: z.enum([
    "mi_nacion",
    "mi_ranking",
    "mi_prediccion",
    "mi_clip_viral",
    "batalla_naciones",
    "mision_completada",
    "nuevo_badge",
    "nuevo_nivel",
  ]),
  nation: z.string().min(1).max(64).nullish(),
  rivalNation: z.string().min(1).max(64).nullish(),
  headline: z.string().min(1).max(200).nullish(),
  subline: z.string().min(1).max(200).nullish(),
  position: z.number().int().min(1).max(100000).nullish(),
  viralScore: z.number().int().min(0).max(100).nullish(),
  platform: z.string().min(1).max(32).nullish(),
});

export const recordViralClip = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => Input.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const row = {
      user_id: userId,
      match_id: `personal:${data.kind}`,
      home_team: data.nation ?? null,
      away_team: data.rivalNation ?? null,
      score: data.position ? `#${data.position}` : (data.headline ?? null)?.slice(0, 80) ?? null,
      minute: data.viralScore != null
        ? `v${data.viralScore}`
        : (data.subline ?? null)?.slice(0, 60) ?? null,
      emotion: data.kind,
      platform: data.platform ?? "versesport",
      views: 0,
      shares: 0,
      clicks: 0,
    };
    const { data: inserted, error } = await supabase
      .from("viral_clips")
      .insert(row)
      .select("id, created_at")
      .single();
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, id: inserted.id, createdAt: inserted.created_at };
  });